import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  toastSvrNotificationWarnings,
} from "@/components/site-visits/SvrInventoryTeamFields";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  MapPin,
  Search,
  UserPlus,
  Users,
  X,
  UserX,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePathname, navigate } from "wouter/use-browser-location";
import { cn } from "@/lib/utils";
import {
  formatSingaporeDate,
  getSingaporeDateKeyFromInstant,
  getSingaporeMinutesOfDay,
  parseSingaporeDateInput,
  singaporeCalendarRangeBounds,
  singaporeDateKeysEqual,
  singaporeTodayDateKey,
} from "@shared/timezone";
import {
  addDays as addSingaporeDays,
  coerceValidDay,
} from "@/components/field-calendar/fieldCalendarUtils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  SITE_VISIT_SLOT_END_HOUR as SLOT_END_HOUR,
  SITE_VISIT_SLOT_START_HOUR as SLOT_START_HOUR,
  SITE_VISIT_TOTAL_SLOTS as TOTAL_SLOTS,
  SITE_VISIT_SLOT_WINDOWS as SLOT_WINDOWS,
  buildArrivalWindowFromSlots,
  siteVisitSlotLabel as slotLabel,
  windowToSlot,
} from "@shared/siteVisitCalendarWindows";
import {
  dragRangeToArrivalWindow,
  parseValidArrivalWindow,
  partitionCalendarVisits,
} from "@shared/svrSchedulingIntegrity";
import { CalendarVisitsLoadError, SchedulingIncompleteSection } from "@/components/field-calendar/CalendarVisitLoadState";

// ─── Constants ────────────────────────────────────────────────────────────────
/** Unified grid: time column + N day columns — must match across header and body (no sub-layout offsets). */
const TIME_COL_PX = 72;
const HEADER_ROW_PX = 72;
const SLOT_PX = 72;

const STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  pending_review: "Pending Review",
  report_sent: "Report Sent",
  awaiting_payment: "Awaiting Payment",
  closed: "Closed",
  waived: "Waived",
};

const ROLE_COLORS: Record<string, string> = {
  technician: "bg-blue-500",
  programmer: "bg-violet-500",
  installer: "bg-emerald-500",
};

// Static block colour classes for calendar visit blocks (Tailwind-safe)
const ROLE_BLOCK_COLORS: Record<string, string> = {
  technician: "bg-blue-100 border-blue-300 text-blue-900",
  programmer: "bg-violet-100 border-violet-300 text-violet-900",
  installer: "bg-emerald-100 border-emerald-300 text-emerald-900",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function addDays(date: Date, n: number): Date {
  // Prefer Singapore calendar-day math for navigation/display consistency.
  return addSingaporeDays(coerceValidDay(date), n);
}
/** Embedded dashboard: if today is Sunday, start the 3-day window on Monday. */
function embeddedDashboardAnchorDate(): Date {
  const today = coerceValidDay(new Date());
  // Sunday in Singapore → start Monday
  const key = getSingaporeDateKeyFromInstant(today);
  if (!key) return today;
  const wd = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Singapore",
    weekday: "short",
  }).format(today);
  if (wd === "Sun") return addSingaporeDays(today, 1);
  return today;
}

type Customer = {
  id: number;
  name: string;
  contact: string | null;
  email: string | null;
  address: string | null;
  unitNumber: string | null;
  postalCode: string | null;
};

export type TeamCalendarProps = {
  /** Embed on service dashboard — skips page chrome and uses compact layout. */
  embedded?: boolean;
  initialViewDays?: 1 | 3 | 7;
};

/** Embedded dashboard: all role lanes on, personal filter off. */
function embeddedTeamScheduleDefaults() {
  return { mySchedule: false, hiddenRoles: new Set<string>() };
}

function initialScheduleFilterState(embedded: boolean) {
  if (embedded) return embeddedTeamScheduleDefaults();
  return {
    mySchedule: window.innerWidth < 768,
    hiddenRoles: new Set<string>(),
  };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function TeamCalendar({ embedded = false, initialViewDays }: TeamCalendarProps = {}) {
  const pathname = usePathname();

  // ── Auth (for MY SCHEDULE filter) ─────────────────────────────────────
  const { user: currentUser } = useAuth();

  // ── Role filter + MY SCHEDULE ────────────────────────────────────────────
  // hiddenRoles: set of role strings the user has toggled OFF
  // mySchedule: when true, show only visits where the logged-in user is an assignee
  const scheduleFiltersTouchedRef = useRef(false);
  const [hiddenRoles, setHiddenRoles] = useState<Set<string>>(
    () => initialScheduleFilterState(embedded).hiddenRoles,
  );
  const [mySchedule, setMySchedule] = useState(
    () => initialScheduleFilterState(embedded).mySchedule,
  );

  // Embedded embed must always land on combined team view (not mobile “My Schedule”).
  useLayoutEffect(() => {
    if (!embedded || scheduleFiltersTouchedRef.current) return;
    const defaults = embeddedTeamScheduleDefaults();
    setMySchedule(defaults.mySchedule);
    setHiddenRoles(defaults.hiddenRoles);
  }, [embedded]);

  function toggleRole(role: string) {
    scheduleFiltersTouchedRef.current = true;
    // Switching to a role tab turns off MY SCHEDULE
    setMySchedule(false);
    setHiddenRoles(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role); else next.add(role);
      return next;
    });
  }

  function activateMySchedule() {
    scheduleFiltersTouchedRef.current = true;
    setMySchedule(true);
    setHiddenRoles(new Set()); // reset role filters when switching to MY SCHEDULE
  }

  // ── Mobile detection (single source of truth) ───────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── View days: default Day (1) on mobile, 3-Day on desktop ──────────────
  const [viewDays, setViewDays] = useState<1 | 3 | 7>(() => {
    if (embedded) return initialViewDays ?? 3;
    return initialViewDays ?? (window.innerWidth < 768 ? 1 : 3);
  });

  // When screen resizes to mobile, collapse week view on full calendar page only
  useEffect(() => {
    if (embedded) return;
    if (isMobile && viewDays === 7) setViewDays(1);
  }, [isMobile, viewDays, embedded]);

  // ── Calendar navigation — always anchored to today by default ───────────
  const [currentDate, setCurrentDate] = useState(() =>
    embedded ? embeddedDashboardAnchorDate() : coerceValidDay(new Date()),
  );

  const { startDate, endDate, firstDay, lastDay, days: weekDays } = useMemo(() => {
    const first = coerceValidDay(currentDate);
    const last = addSingaporeDays(first, viewDays - 1);
    const { startInclusive, endExclusive } = singaporeCalendarRangeBounds(first, last);
    const days: Date[] = [];
    for (let i = 0; i < viewDays; i++) days.push(addSingaporeDays(first, i));
    return {
      startDate: startInclusive,
      endDate: endExclusive,
      firstDay: first,
      lastDay: last,
      days,
    };
  }, [currentDate, viewDays]);

  const visitsQuery = trpc.siteVisits.teamCalendarVisits.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as any
  );
  const visitsLoadFailed = Boolean(visitsQuery.isError);
  const visits = visitsLoadFailed ? [] : (visitsQuery.data ?? []);
  const isLoading = visitsQuery.isLoading;
  const { data: leaveBlocks = [] } = trpc.siteVisits.teamCalendarLeaveBlocks.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as any
  );

  const utils = trpc.useUtils();

  function shiftCalendarView(direction: -1 | 1) {
    setCurrentDate((d) => addDays(d, direction * viewDays));
  }

  const today = useMemo(() => coerceValidDay(new Date()), []);

  /** Ticks for “now” line; 30s keeps alignment fresh without wide styling churn. */
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    setClock(new Date());
    const id = window.setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(id);
  }, [currentDate, viewDays]);

  const title = viewDays === 1
    ? formatSingaporeDate(firstDay, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : `${formatSingaporeDate(firstDay, { day: "numeric", month: "short" })} – ${formatSingaporeDate(lastDay, { day: "numeric", month: "short", year: "numeric" })}`;

  const nowTimeLine = useMemo(() => {
    const todayIdx = weekDays.findIndex((d) => singaporeDateKeysEqual(d, clock));
    if (todayIdx < 0) return null;
    const totalMins = getSingaporeMinutesOfDay(clock);
    if (totalMins == null) return null;
    const startMins = SLOT_START_HOUR * 60;
    const endMins = SLOT_END_HOUR * 60;
    if (totalMins < startMins || totalMins >= endMins) return null;
    const topPx = HEADER_ROW_PX + ((totalMins - startMins) / 30) * SLOT_PX;
    const label = clock.toLocaleTimeString("en-SG", { hour: "numeric", minute: "2-digit", hour12: true });
    return { todayIdx, topPx, label };
  }, [weekDays, clock]);

  // ── Filtered visits (MY SCHEDULE or role filter) ───────────────────────
  const filteredVisits = useMemo(() => {
    if (mySchedule && currentUser) {
      return visits.filter(v => {
        const assignees: any[] = (v as any).assignees ?? [];
        if (assignees.some((a: any) => a.userId === currentUser.id)) return true;
        // Legacy rows with only primary technicianId and no assignee rows
        return (v as any).technicianId === currentUser.id;
      });
    }
    if (hiddenRoles.size === 0) return visits;
    return visits.filter(v => {
      const assignees: any[] = (v as any).assignees ?? [];
      if (assignees.length === 0) return true; // unassigned visits always show
      // Show visit if ANY assignee has a role that is NOT hidden
      return assignees.some((a: any) => !hiddenRoles.has(a.role));
    });
  }, [visits, mySchedule, currentUser, hiddenRoles]);

  const { timed: timedVisits, incomplete: incompleteVisits } = useMemo(
    () => partitionCalendarVisits(filteredVisits),
    [filteredVisits],
  );

  // Group visits by date + slot index
  // If arrivedAt is set, use actual arrival time for slot placement; otherwise use arrivalWindow
  const visitsByDateAndSlot = useMemo(() => {
    const map: Record<string, Record<number, typeof visits>> = {};
    for (const v of timedVisits) {
      const dateKey =
        (v as any).arrivedAt != null
          ? getSingaporeDateKeyFromInstant((v as any).arrivedAt)
          : getSingaporeDateKeyFromInstant(v.visitDate);
      if (!dateKey) continue;

      let slotIdx: number;
      if ((v as any).arrivedAt) {
        // Use actual arrival time → convert to slot index (Singapore clock).
        const totalMins = getSingaporeMinutesOfDay((v as any).arrivedAt) ?? 0;
        const startMins = SLOT_START_HOUR * 60;
        slotIdx = Math.floor((totalMins - startMins) / 30);
        if (slotIdx < 0) slotIdx = 0;
        if (slotIdx >= TOTAL_SLOTS) slotIdx = TOTAL_SLOTS - 1;
      } else {
        const win = v.arrivalWindow;
        if (!win) continue;
        slotIdx = windowToSlot(win);
        if (slotIdx < 0) continue;
      }

      if (!map[dateKey]) map[dateKey] = {};
      if (!map[dateKey][slotIdx]) map[dateKey][slotIdx] = [];
      map[dateKey][slotIdx].push(v);
    }
    return map;
  }, [timedVisits]);

  // ── Desktop drag state ───────────────────────────────────────────────────
  const [dragStart, setDragStart] = useState<{ dayIdx: number; rowIdx: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ dayIdx: number; rowIdx: number } | null>(null);
  const isDragging = dragStart !== null;
  // Ref mirror of isDragging — avoids stale closures in onMouseEnter callbacks
  const isDraggingRef = useRef(false);

  // ── Mobile long-press state ──────────────────────────────────────────────
  // tapPhase: null = idle, "pressing" = long-press in progress (slot glows), "first" = start confirmed, waiting for end
  const [tapPhase, setTapPhase] = useState<null | "pressing" | "first">(null);
  const [tapStart, setTapStart] = useState<{ dayIdx: number; rowIdx: number } | null>(null);
  // Pending slot being held (shows glow before confirmed)
  const [pressingCell, setPressingCell] = useState<{ dayIdx: number; rowIdx: number } | null>(null);
  // Long-press timer ref
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track touch start position to distinguish a tap from a scroll gesture
  const touchOrigin = useRef<{ x: number; y: number } | null>(null);

  // ── Slide-in form panel ──────────────────────────────────────────────────
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [arrivalWindow, setArrivalWindow] = useState<string>("");
  // For multi-slot selection (desktop drag or mobile two-tap)
  const [arrivalWindowEnd, setArrivalWindowEnd] = useState<string>("");
  const scheduledWindowRef = useRef<string>("");

  function commitDragRange(startRow: number, endRow: number) {
    const windowValue = dragRangeToArrivalWindow(startRow, endRow);
    scheduledWindowRef.current = windowValue;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    setArrivalWindow(SLOT_WINDOWS[minRow] ?? "");
    setArrivalWindowEnd(SLOT_WINDOWS[maxRow] ?? "");
    return windowValue;
  }

  // ── Form fields ──────────────────────────────────────────────────────────
  const [visitType, setVisitType] = useState<"new_install" | "service_visit">("service_visit");
  const [complaintDescription, setComplaintDescription] = useState("");
  const [selectedAssignees, setSelectedAssignees] = useState<{ userId: number; userName: string; role: string }[]>([]);
  const [preAgreedChargeEnabled, setPreAgreedChargeEnabled] = useState(false);
  const [preAgreedChargeAmount, setPreAgreedChargeAmount] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveUserId, setLeaveUserId] = useState<number | null>(null);
  const [leaveDate, setLeaveDate] = useState("");
  const [leavePeriod, setLeavePeriod] = useState<"full_day" | "am" | "pm">("full_day");
  const [leaveNotes, setLeaveNotes] = useState("");
  const [selectedLeaveIds, setSelectedLeaveIds] = useState<number[]>([]);

  // ── Customer search ──────────────────────────────────────────────────────
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const calendarScrollRef = useRef<HTMLDivElement>(null);

  // Scroll to current time once the grid is rendered (fires after isLoading becomes false)
  useEffect(() => {
    if (isLoading) return;
    // Short timeout ensures the DOM has painted before measuring clientHeight
    const timer = setTimeout(() => {
      const el = calendarScrollRef.current;
      if (!el) return;
      const now = new Date();
      const currentHour = now.getHours();
      const currentMin = now.getMinutes();
      const slotIdx = Math.max(0, (currentHour - SLOT_START_HOUR) * 2 + Math.floor(currentMin / 30));
      const scrollTop = Math.max(0, slotIdx * SLOT_PX - el.clientHeight / 2 + SLOT_PX / 2);
      el.scrollTop = scrollTop;
    }, 50);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // ── Inline create customer modal ─────────────────────────────────────────
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustForm, setNewCustForm] = useState({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false });
  const [newCustAddressQuery, setNewCustAddressQuery] = useState("");

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: searchResults } = trpc.customers.searchForServiceVisit.useQuery(
    { query: customerQuery },
    { enabled: customerQuery.length >= 2 && !selectedCustomer }
  );
  const { data: allUsers } = trpc.siteVisits.listStaff.useQuery();
  const technicians = allUsers?.filter(u => ["service", "admin"].includes(u.role)) ?? [];
  /** Staff who can be scheduled on visits / appear in leave picker (admin sees all of these). */
  const leaveScheduleUsers = useMemo(
    () =>
      (allUsers ?? [])
        .filter((u) => ["service", "installer", "programming", "admin"].includes(u.role))
        .slice()
        .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""))),
    [allUsers]
  );
  const programmers = allUsers?.filter(u => u.role === "programming") ?? [];
  const installers = allUsers?.filter(u => u.role === "installer") ?? [];
  const isLeaveAdmin = currentUser?.role === "admin";

  const handleLeaveDialogChange = useCallback(
    (open: boolean) => {
      setLeaveOpen(open);
      if (open) {
        setSelectedLeaveIds([]);
        if (isLeaveAdmin) {
          setLeaveUserId(null);
        } else if (currentUser) {
          setLeaveUserId(currentUser.id);
        }
      }
    },
    [isLeaveAdmin, currentUser]
  );

  const visitsInViewRange = useMemo(() => {
    const startKey = getSingaporeDateKeyFromInstant(firstDay);
    const endKey = getSingaporeDateKeyFromInstant(lastDay);
    if (!startKey || !endKey) return visits;
    return visits.filter((v) => {
      const key =
        (v as any).arrivedAt != null
          ? getSingaporeDateKeyFromInstant((v as any).arrivedAt)
          : getSingaporeDateKeyFromInstant(v.visitDate);
      return key != null && key >= startKey && key <= endKey;
    });
  }, [visits, firstDay, lastDay]);

  const summaryStats = useMemo(
    () => ({
      scheduled: visitsInViewRange.filter((v) => v.status === "scheduled").length,
      completed: visitsInViewRange.filter((v) => v.status === "closed").length,
      pending: visitsInViewRange.filter((v) =>
        ["pending_review", "report_sent", "awaiting_payment"].includes(v.status)
      ).length,
      teamMembers: allUsers?.length ?? 0,
    }),
    [visitsInViewRange, allUsers]
  );

  function toggleAssignee(userId: number, userName: string, role: string) {
    setSelectedAssignees(prev => {
      const exists = prev.some(a => a.userId === userId);
      if (exists) return prev.filter(a => a.userId !== userId);
      return [...prev, { userId, userName, role }];
    });
  }

  function leavePeriodForWindow(win: string | null | undefined): "am" | "pm" {
    const slot = windowToSlot(win || "");
    if (slot < 0) return "am";
    const totalMins = SLOT_START_HOUR * 60 + slot * 30;
    return totalMins < 12 * 60 ? "am" : "pm";
  }

  function getLeaveForUser(userId: number, date: Date | null, periodHint: "am" | "pm"): { period: string; notes: string | null } | null {
    if (!date) return null;
    const hit = leaveBlocks.find((lb: any) => {
      if (lb.userId !== userId || !singaporeDateKeysEqual(lb.leaveDate, date)) return false;
      return lb.period === "full_day" || lb.period === periodHint;
    });
    if (!hit) return null;
    return { period: hit.period, notes: hit.notes ?? null };
  }

  // Conflict detection
  function hasConflict(userId: number): { visitNumber: string } | null {
    if (!selectedDate || !arrivalWindow) return null;
    for (const v of visits) {
      if (v.arrivalWindow !== arrivalWindow) continue;
      if (!singaporeDateKeysEqual(v.visitDate, selectedDate)) continue;
      const assignees: any[] = (v as any).assignees ?? [];
      if (assignees.some((a: any) => a.userId === userId)) return { visitNumber: v.visitNumber };
    }
    return null;
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Mutations ────────────────────────────────────────────────────────────
  const createVisit = trpc.siteVisits.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Service visit ${data.visitNumber} created`);
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      utils.siteVisits.teamCalendarVisits.invalidate();
      setPanelOpen(false);
      resetPanel();
      navigate(`/site-visits/id/${data.id}`, { state: { from: pathname } });
    },
    onError: (err) => toast.error(err.message || "Failed to create service visit"),
  });
  const createLeave = trpc.siteVisits.createTechnicianLeaveBlock.useMutation({
    onSuccess: (r) => {
      toast.success(r.duplicated ? "Leave block already exists." : `Leave block added${r.alertsSent ? `, ${r.alertsSent} PIC alert(s) sent` : ""}.`);
      void utils.siteVisits.teamCalendarLeaveBlocks.invalidate();
      void utils.siteVisits.listTechnicianLeaveBlocksForUser.invalidate();
      setLeaveOpen(false);
      setLeaveUserId(null);
      setLeaveDate("");
      setLeavePeriod("full_day");
      setLeaveNotes("");
      setSelectedLeaveIds([]);
    },
    onError: (e) => toast.error(e.message || "Failed to add leave block"),
  });

  const { data: managedLeaveRows = [] } = trpc.siteVisits.listTechnicianLeaveBlocksForUser.useQuery(
    { userId: leaveUserId! },
    { enabled: leaveOpen && leaveUserId != null && leaveUserId > 0 }
  );

  const deleteLeaveBlocks = trpc.siteVisits.deleteTechnicianLeaveBlocks.useMutation({
    onSuccess: async (r) => {
      toast.success(r.deleted === 1 ? "Leave cancelled." : `${r.deleted} leave blocks cancelled.`);
      setSelectedLeaveIds([]);
      await utils.siteVisits.teamCalendarLeaveBlocks.invalidate();
      await utils.siteVisits.listTechnicianLeaveBlocksForUser.invalidate();
    },
    onError: (e) => toast.error(e.message || "Could not cancel leave."),
  });

  useEffect(() => {
    setSelectedLeaveIds([]);
  }, [leaveUserId]);

  useEffect(() => {
    if (!leaveOpen) setSelectedLeaveIds([]);
  }, [leaveOpen]);

  useEffect(() => {
    if (!leaveOpen || isLeaveAdmin || !currentUser?.id) return;
    setLeaveUserId(currentUser.id);
  }, [leaveOpen, isLeaveAdmin, currentUser?.id]);

  const createCustomerInline = trpc.customers.create.useMutation({
    onSuccess: (data) => {
      setSelectedCustomer({ id: data.id, name: newCustForm.name.trim(), contact: newCustForm.contact.trim() || null, email: newCustForm.email.trim() || null, address: newCustForm.address.trim() || null, unitNumber: newCustForm.unitNumber.trim() || null, postalCode: newCustForm.postalCode.trim() || null });
      setCustomerQuery(newCustForm.name.trim());
      setShowDropdown(false);
      setShowNewCustomerModal(false);
      setNewCustForm({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false });
      setNewCustAddressQuery("");
      utils.customers.list.invalidate();
      toast.success(`Customer "${newCustForm.name.trim()}" created`);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  // ── Desktop drag handlers ────────────────────────────────────────────
  const applyFieldStaffDefaultAssignees = useCallback(() => {
    if (!currentUser) return;
    const role = currentUser.role;
    if (role !== "service" && role !== "programming" && role !== "installer") return;
    const assigneeRole =
      role === "programming" ? "programmer" : role === "installer" ? "installer" : "technician";
    setSelectedAssignees([
      {
        userId: currentUser.id,
        userName: currentUser.name ?? currentUser.email ?? `User ${currentUser.id}`,
        role: assigneeRole,
      },
    ]);
  }, [currentUser]);

  const openSchedulePanel = useCallback(() => {
    applyFieldStaffDefaultAssignees();
    setPanelOpen(true);
  }, [applyFieldStaffDefaultAssignees]);

  const handleCellMouseDown = useCallback((dayIdx: number, rowIdx: number) => {
    isDraggingRef.current = true;
    setDragStart({ dayIdx, rowIdx });
    setDragEnd({ dayIdx, rowIdx });
  }, []);

  const handleCellMouseEnter = useCallback((dayIdx: number, rowIdx: number) => {
    // Use ref instead of isDragging state to avoid stale closure — the state update from
    // mousedown may not have propagated yet when the first mouseenter fires.
    if (isDraggingRef.current) setDragEnd({ dayIdx, rowIdx });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (dragStart) {
      const day = weekDays[dragStart.dayIdx];
      const minRow = Math.min(dragStart.rowIdx, dragEnd?.rowIdx ?? dragStart.rowIdx);
      const maxRow = Math.max(dragStart.rowIdx, dragEnd?.rowIdx ?? dragStart.rowIdx);
      setSelectedDate(day);
      commitDragRange(minRow, maxRow);
      openSchedulePanel();
    }
    isDraggingRef.current = false;
    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, weekDays, openSchedulePanel]);

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseUp]);

  const isCellSelected = useCallback((dayIdx: number, rowIdx: number) => {
    if (!dragStart || !dragEnd) return false;
    const minDay = Math.min(dragStart.dayIdx, dragEnd.dayIdx);
    const maxDay = Math.max(dragStart.dayIdx, dragEnd.dayIdx);
    const minRow = Math.min(dragStart.rowIdx, dragEnd.rowIdx);
    const maxRow = Math.max(dragStart.rowIdx, dragEnd.rowIdx);
    return dayIdx >= minDay && dayIdx <= maxDay && rowIdx >= minRow && rowIdx <= maxRow;
  }, [dragStart, dragEnd]);

  // ── Mobile two-tap handler ───────────────────────────────────────────────
  // First tap: highlight the start slot and wait for second tap
  // Second tap on same or later row: open modal with that range
  // Second tap on earlier row: treat it as a new first tap
  const handleCellTap = useCallback((dayIdx: number, rowIdx: number) => {
    const day = weekDays[dayIdx];
    if (!day) return;
    const dayKey = getSingaporeDateKeyFromInstant(day);
    const isPast = dayKey != null && dayKey < singaporeTodayDateKey();
    if (isPast) return;

    if (tapPhase === null) {
      // First tap — highlight start slot
      setTapStart({ dayIdx, rowIdx });
      setTapPhase("first");
    } else {
      // Second tap
      if (tapStart && tapStart.dayIdx === dayIdx && rowIdx >= tapStart.rowIdx) {
        // Valid second tap — open modal with range
        setSelectedDate(weekDays[tapStart.dayIdx]);
        commitDragRange(tapStart.rowIdx, rowIdx);
        setTapStart(null);
        setTapPhase(null);
        openSchedulePanel();
      } else {
        // Tap on different day or earlier row — restart as new first tap
        setTapStart({ dayIdx, rowIdx });
        setTapPhase("first");
      }
    }
  }, [tapPhase, tapStart, weekDays, today, openSchedulePanel]);

  // Cancel tap selection if panel closes without completing
  const cancelTap = useCallback(() => {
    setTapStart(null);
    setTapPhase(null);
  }, []);

  const isCellTapHighlighted = useCallback((dayIdx: number, rowIdx: number) => {
    if (!tapStart || tapPhase !== "first") return false;
    return tapStart.dayIdx === dayIdx && tapStart.rowIdx === rowIdx;
  }, [tapStart, tapPhase]);

  // ── Submit ───────────────────────────────────────────────────────────────
  // Build a combined HH:MM-HH:MM arrival window from start slot + end slot
  const buildArrivalWindowValue = (): string | undefined => {
    if (scheduledWindowRef.current) return scheduledWindowRef.current;
    if (!arrivalWindow) return undefined;
    const startSlot = windowToSlot(arrivalWindow);
    if (startSlot < 0) return arrivalWindow;
    const endSlot = arrivalWindowEnd ? windowToSlot(arrivalWindowEnd) : startSlot;
    return buildArrivalWindowFromSlots(startSlot, endSlot) ?? arrivalWindow;
  };

  const handleSubmit = () => {
    if (!selectedCustomer) { toast.error("Please select a customer"); return; }
    if (!selectedDate) { toast.error("Please select a visit date"); return; }
    if (!complaintDescription.trim()) { toast.error("Scope of Work is required before scheduling a visit"); return; }
    const authoritativeWindow = parseValidArrivalWindow(buildArrivalWindowValue());
    if (!authoritativeWindow) {
      toast.error("A scheduled visit requires a valid start and end time.");
      return;
    }
    if (preAgreedChargeEnabled && (!preAgreedChargeAmount || Number(preAgreedChargeAmount) <= 0)) {
      toast.error("Pre-agreed visit charge amount is required");
      return;
    }
    createVisit.mutate({
      customerId: selectedCustomer.id,
      visitType: "planned",
      visitDate: parseSingaporeDateInput(getSingaporeDateKeyFromInstant(selectedDate)!),
      arrivalWindow: authoritativeWindow,
      complaintDescription: complaintDescription || undefined,
      assignees: selectedAssignees.map(a => ({ userId: a.userId, userName: a.userName, role: a.role as "technician" | "programmer" | "installer" })),
      inventoryTeamRequested: false,
      inventoryTeamMessage: null,
      preAgreedChargeEnabled,
      preAgreedChargeAmount: preAgreedChargeEnabled ? Number(preAgreedChargeAmount) : undefined,
    });
  };

  const resetPanel = () => {
    setSelectedCustomer(null);
    setCustomerQuery("");
    setComplaintDescription("");
    setSelectedAssignees([]);
    setVisitType("service_visit");
    setArrivalWindow("");
    setArrivalWindowEnd("");
    scheduledWindowRef.current = "";
    setSelectedDate(null);
    setPreAgreedChargeEnabled(false);
    setPreAgreedChargeAmount("");
  };

  const fullAddress = selectedCustomer
    ? [selectedCustomer.unitNumber, selectedCustomer.address, selectedCustomer.postalCode].filter(Boolean).join(", ")
    : "";

  // Build time label for modal header
  const timeLabel = useMemo(() => {
    if (!arrivalWindow) return "";
    const startSlot = windowToSlot(arrivalWindow);
    const endSlot = arrivalWindowEnd ? windowToSlot(arrivalWindowEnd) : startSlot;
    if (startSlot < 0) return arrivalWindow;
    const startStr = slotLabel(startSlot);
    if (endSlot < 0 || endSlot === startSlot) return startStr;
    const endStr = endSlot + 1 < TOTAL_SLOTS ? slotLabel(endSlot + 1) : "8:00 PM";
    return `${startStr} – ${endStr}`;
  }, [arrivalWindow, arrivalWindowEnd]);

  const calendarInner = (
    <>
      <div
        className={
          embedded
            ? "space-y-3 select-none"
            : "mx-auto w-full max-w-[min(100%,1680px)] px-3 py-5 sm:px-5 space-y-5 select-none"
        }
      >

        {/* ── Header ── */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {!embedded ? (
              <div className="flex items-center gap-3">
                <CalendarDays className="h-6 w-6 text-muted-foreground" />
                <div>
                  <h1 className="text-xl font-semibold">Team Calendar</h1>
                  <p className="text-xs text-muted-foreground">
                    {isMobile
                      ? tapPhase === "first"
                        ? "Now tap the end slot to set the duration"
                        : "Tap a slot to start, tap again to set end time"
                      : "Drag across slots to create a visit"}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {isMobile
                  ? "Tap slots to create a visit"
                  : "Drag slots or click a visit to open"}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => handleLeaveDialogChange(true)}>
                <UserX className="mr-1 h-4 w-4" />
                ON LEAVE
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setCurrentDate(new Date()); cancelTap(); }}>Today</Button>
              {/* Day / 3-Day / Week toggle */}
              <div className="flex rounded-md border overflow-hidden text-xs">
                {([1, 3, 7] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => { setViewDays(v); cancelTap(); }}
                    className={`px-2.5 py-1 font-medium transition-colors ${
                      viewDays === v ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {v === 1 ? "Day" : v === 3 ? "3 Days" : "Week"}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={() => { shiftCalendarView(-1); cancelTap(); }}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium text-center min-w-[120px]">{title}</span>
              <Button size="sm" variant="outline" onClick={() => { shiftCalendarView(1); cancelTap(); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        {visitsLoadFailed ? (
          <CalendarVisitsLoadError onRetry={() => void visitsQuery.refetch()} />
        ) : null}
        <SchedulingIncompleteSection
          visits={incompleteVisits}
          onOpen={(id) => navigate(`/site-visits/id/${id}`, { state: { from: pathname } })}
        />

        {/* ── Mobile tap hint banner ── */}
        {isMobile && tapPhase === "first" && tapStart && (
          <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
            <span>
              Start: <strong>{slotLabel(tapStart.rowIdx)}</strong> — now tap the end slot
            </span>
            <button onClick={cancelTap} className="p-1 rounded hover:bg-amber-100">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* ── Role filter + MY SCHEDULE ── */}
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-medium text-muted-foreground">View:</span>
          {/* MY SCHEDULE button */}
          <button
            onClick={activateMySchedule}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-medium transition-colors ${
              mySchedule
                ? "bg-black text-white border-black"
                : "bg-white text-gray-500 border-gray-300 hover:border-gray-400"
            }`}
          >
            My Schedule
          </button>
          {/* Role toggle buttons */}
          {(Object.entries(ROLE_BLOCK_COLORS) as [string, string][]).map(([role, cls]) => {
            const isActive = !mySchedule && !hiddenRoles.has(role);
            return (
              <button
                key={role}
                onClick={() => toggleRole(role)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-medium transition-colors capitalize ${
                  isActive
                    ? `${cls} opacity-100`
                    : "bg-white text-gray-400 border-gray-200 line-through opacity-60"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${isActive ? ROLE_COLORS[role] : "bg-gray-300"}`} />
                {role}
              </button>
            );
          })}
        </div>

        {/* ── Calendar Grid — one unified grid (time column + N×1fr day columns) ── */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">Loading calendar…</div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_40px_-24px_rgba(15,23,42,0.12)]">
            <div
              ref={calendarScrollRef}
              className={
                embedded
                  ? "max-h-[min(420px,55vh)] overflow-y-auto overflow-x-hidden"
                  : "max-h-[min(560px,calc(100vh-15rem))] overflow-y-auto overflow-x-hidden"
              }
            >
              <div
                className="relative w-full"
                style={{ minHeight: HEADER_ROW_PX + TOTAL_SLOTS * SLOT_PX }}
              >
                <div
                  className="grid w-full [align-content:start]"
                  style={{
                    gridTemplateColumns: `${TIME_COL_PX}px repeat(${viewDays}, minmax(0, 1fr))`,
                    gridTemplateRows: `${HEADER_ROW_PX}px repeat(${TOTAL_SLOTS}, ${SLOT_PX}px)`,
                  }}
                >
                  <div
                    className="sticky top-0 z-30 border-b border-r border-slate-200/80 bg-slate-50/95 pr-1.5"
                    style={{ gridColumn: 1, gridRow: 1 }}
                    aria-hidden
                  />
                  {weekDays.map((day, i) => {
                    const isTodayH = singaporeDateKeysEqual(day, singaporeTodayDateKey());
                    return (
                      <div
                        key={day.toDateString()}
                        className={cn(
                          "sticky top-0 z-30 border-b border-r border-slate-200/80 bg-slate-50/95 px-1.5 py-2 text-center last:border-r-0",
                          i % 2 === 1 && "bg-slate-100/70",
                          isTodayH &&
                            "z-30 bg-sky-50/95 ring-1 ring-inset ring-slate-800/10 shadow-[inset_0_0_0_1px_rgba(14,165,233,0.12)]"
                        )}
                        style={{ gridColumn: i + 2, gridRow: 1 }}
                      >
                        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                          {formatSingaporeDate(day, { weekday: "short" })}
                        </div>
                        <div
                          className={cn(
                            "mx-auto mt-1.5 flex h-8 w-8 items-center justify-center text-sm font-semibold tabular-nums",
                            isTodayH
                              ? "rounded-full bg-slate-900 text-white shadow"
                              : "text-slate-800"
                          )}
                        >
                          {formatSingaporeDate(day, { day: "numeric" })}
                        </div>
                        {leaveBlocks
                          .filter((lb: any) => singaporeDateKeysEqual(lb.leaveDate, day))
                          .slice(0, 2)
                          .map((lb: any) => (
                            <div
                              key={`leave-${lb.id}`}
                              className="mx-auto mt-1 max-w-full truncate rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-medium text-rose-700"
                              title={`${lb.userName ?? `User ${lb.userId}`} on leave (${lb.period})${lb.notes ? `: ${lb.notes}` : ""}`}
                            >
                              {(lb.userName ?? `User ${lb.userId}`).split(" ")[0]} ({lb.period === "full_day" ? "FD" : String(lb.period).toUpperCase()})
                            </div>
                          ))}
                      </div>
                    );
                  })}

                  {Array.from({ length: TOTAL_SLOTS }, (_, rowIdx) => {
                    const isHourBoundary = rowIdx % 2 === 0;
                    return (
                      <div key={`slot-row-${rowIdx}`} className="contents">
                        <div
                          className={cn(
                            "sticky left-0 z-20 border-r border-slate-200/70 bg-slate-50/40 pr-2",
                            isHourBoundary
                              ? "border-b border-slate-200/80 py-2"
                              : "border-b border-dashed border-slate-200/45 py-0.5"
                          )}
                          style={{ gridColumn: 1, gridRow: rowIdx + 2 }}
                        >
                          {isHourBoundary && (
                            <span className="block pr-0.5 text-right text-[11px] font-medium leading-none text-slate-500 tabular-nums">
                              {slotLabel(rowIdx)}
                            </span>
                          )}
                        </div>
                        {weekDays.map((day, dayIdx) => {
                          const dayKey = getSingaporeDateKeyFromInstant(day);
                          const isPast = dayKey != null && dayKey < singaporeTodayDateKey();
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          const isTodayCol = singaporeDateKeysEqual(day, singaporeTodayDateKey());
                          const isDragSelected = !isMobile && isCellSelected(dayIdx, rowIdx);
                          const isTapSelected = isMobile && isCellTapHighlighted(dayIdx, rowIdx);
                          const isTapHint =
                            isMobile &&
                            tapPhase === "first" &&
                            tapStart &&
                            tapStart.dayIdx === dayIdx &&
                            rowIdx > tapStart.rowIdx;
                          const isPressing =
                            isMobile && pressingCell?.dayIdx === dayIdx && pressingCell?.rowIdx === rowIdx;
                          return (
                            <div
                              key={`cell-${rowIdx}-${dayIdx}`}
                              onMouseDown={() => !isMobile && !isPast && handleCellMouseDown(dayIdx, rowIdx)}
                              onMouseEnter={() => !isMobile && handleCellMouseEnter(dayIdx, rowIdx)}
                              onTouchStart={(e) => {
                                if (!isMobile || isPast) return;
                                touchOrigin.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                                if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
                                setPressingCell({ dayIdx, rowIdx });
                                pressTimerRef.current = setTimeout(() => {
                                  setPressingCell(null);
                                  handleCellTap(dayIdx, rowIdx);
                                  pressTimerRef.current = null;
                                }, 600);
                              }}
                              onTouchMove={(e) => {
                                const origin = touchOrigin.current;
                                if (origin) {
                                  const dx = Math.abs(e.touches[0].clientX - origin.x);
                                  const dy = Math.abs(e.touches[0].clientY - origin.y);
                                  if (dx > 10 || dy > 10) {
                                    if (pressTimerRef.current) {
                                      clearTimeout(pressTimerRef.current);
                                      pressTimerRef.current = null;
                                    }
                                    setPressingCell(null);
                                  }
                                }
                              }}
                              onTouchEnd={() => {
                                if (!isMobile) return;
                                if (pressTimerRef.current) {
                                  clearTimeout(pressTimerRef.current);
                                  pressTimerRef.current = null;
                                }
                                setPressingCell(null);
                                touchOrigin.current = null;
                              }}
                              style={{ gridColumn: dayIdx + 2, gridRow: rowIdx + 2 }}
                              className={cn(
                                "relative min-h-0 border-r border-slate-200/60 transition-colors last:border-r-0",
                                !isHourBoundary
                                  ? "border-b border-dashed border-slate-200/40"
                                  : "border-b border-slate-200/78",
                                dayIdx % 2 === 1 ? "bg-slate-50/40" : "bg-white/60",
                                isTodayCol && "ring-1 ring-inset ring-slate-800/8 bg-sky-50/30",
                                isDragSelected && "z-[5] cursor-pointer bg-blue-100/90",
                                isPressing && "z-[5] cursor-pointer bg-amber-100/80",
                                isTapSelected && "z-[5] cursor-pointer bg-amber-200/80",
                                isTapHint && "z-[5] cursor-pointer bg-amber-50/80",
                                isPast && "cursor-not-allowed bg-slate-100/55",
                                !isPast &&
                                  (isWeekend
                                    ? "cursor-pointer hover:bg-sky-50/30"
                                    : "cursor-pointer hover:bg-slate-50/50")
                              )}
                            >
                              {isDragSelected && (
                                <div className="pointer-events-none absolute inset-1 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-blue-400 bg-blue-50/70">
                                  <span className="text-xs font-medium text-blue-600">{timeLabel || "New Visit"}</span>
                                </div>
                              )}
                              {isPressing && (
                                <div className="pointer-events-none absolute inset-1 z-10 flex animate-pulse items-center justify-center rounded-md border-2 border-amber-300 bg-amber-50/80">
                                  <span className="text-xs font-semibold text-amber-700">{slotLabel(rowIdx)}</span>
                                </div>
                              )}
                              {isTapSelected && (
                                <div className="pointer-events-none absolute inset-1 z-10 flex items-center justify-center rounded-md border-2 border-amber-500 bg-amber-100/85">
                                  <span className="text-xs font-semibold text-amber-900">✓ {slotLabel(rowIdx)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div
                  className="pointer-events-none absolute left-0 right-0"
                  style={{ top: HEADER_ROW_PX, height: TOTAL_SLOTS * SLOT_PX, zIndex: 25 }}
                >
                  {nowTimeLine && (
                    <div
                      className="absolute left-0 right-0 z-[30]"
                      style={{ top: nowTimeLine.topPx - HEADER_ROW_PX, transform: "translateY(-50%)" }}
                      aria-hidden
                    >
                      <div className="flex w-full min-w-0">
                        <div
                          className="flex shrink-0 items-center justify-end pr-1.5"
                          style={{ width: TIME_COL_PX }}
                        >
                          <span className="rounded bg-black px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums text-white">
                            {nowTimeLine.label}
                          </span>
                        </div>
                        <div
                          className="grid min-w-0 min-h-0 flex-1"
                          style={{ gridTemplateColumns: `repeat(${viewDays}, minmax(0, 1fr))` }}
                        >
                          {Array.from({ length: viewDays }, (_, i) => (
                            <div key={i} className="min-w-0 min-h-0">
                              {i === nowTimeLine.todayIdx ? (
                                <div className="h-0 w-full border-t-2 border-black" />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {weekDays.map((day, dayIdx) => {
                    const dateKey = getSingaporeDateKeyFromInstant(day) ?? "";
                    const dayVisitMap = new Map<number, (typeof filteredVisits)[0]>();
                    Object.values(visitsByDateAndSlot[dateKey] ?? {}).forEach((slotVisits) =>
                      slotVisits.forEach((v) => dayVisitMap.set(v.id, v))
                    );
                    const dayVisits = Array.from(dayVisitMap.values());
                    return dayVisits.map((v) => {
                      const assignees: any[] = (v as any).assignees ?? [];
                      const staffNames = assignees.length
                        ? assignees.map((a: any) => a.userName.split(" ")[0]).join(", ")
                        : v.technicianName?.split(" ")[0] ?? null;
                      const START_MINS = SLOT_START_HOUR * 60;
                      let startMins: number;
                      let endMins: number;
                      const hasActualTimes = (v as any).arrivedAt != null;
                      if (hasActualTimes) {
                        startMins = getSingaporeMinutesOfDay((v as any).arrivedAt) ?? 0;
                        if ((v as any).departedAt) {
                          endMins = getSingaporeMinutesOfDay((v as any).departedAt) ?? startMins + 30;
                        } else {
                          endMins = getSingaporeMinutesOfDay(new Date()) ?? startMins + 30;
                          if (endMins <= startMins) endMins = startMins + 30;
                        }
                      } else {
                        const win = v.arrivalWindow ?? "";
                        const rangeMatch = win.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
                        if (rangeMatch) {
                          startMins = Number(rangeMatch[1]) * 60 + Number(rangeMatch[2]);
                          endMins = Number(rangeMatch[3]) * 60 + Number(rangeMatch[4]);
                          if (endMins <= startMins) endMins = startMins + 30;
                        } else {
                          const slotIdx = win ? windowToSlot(win) : -1;
                          if (slotIdx < 0) return null;
                          startMins = START_MINS + slotIdx * 30;
                          endMins = startMins + 30;
                        }
                      }
                      const topPx = ((startMins - START_MINS) / 30) * SLOT_PX;
                      const heightPx = Math.max(
                        ((endMins - startMins) / 30) * SLOT_PX,
                        SLOT_PX * 0.5
                      );
                      const visitIndex = dayVisits.indexOf(v);
                      const totalInDay = dayVisits.length;
                      const colWidth = 100 / totalInDay;
                      const leftPct = visitIndex * colWidth;
                      const primaryRole = (assignees[0] as { role?: string } | undefined)?.role ?? "technician";
                      const accent = ROLE_COLORS[primaryRole] ?? "bg-slate-400";
                      const customerName = (v as any).customerName as string | undefined;
                      const addrLine = [(v as any).customerAddress, (v as any).customerPostalCode]
                        .filter(Boolean)
                        .join(" · ");
                      const timeLine = hasActualTimes
                        ? `${new Date((v as any).arrivedAt).toLocaleTimeString("en-SG", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                            timeZone: "Asia/Singapore",
                          })}${
                            (v as any).departedAt
                              ? ` – ${new Date((v as any).departedAt).toLocaleTimeString("en-SG", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                  timeZone: "Asia/Singapore",
                                })}`
                              : ""
                          }`
                        : (() => {
                            const sIdx = Math.max(0, Math.floor((startMins - START_MINS) / 30));
                            const eIdx = Math.min(
                              TOTAL_SLOTS - 1,
                              Math.max(sIdx, Math.ceil((endMins - START_MINS) / 30) - 1)
                            );
                            return sIdx === eIdx
                              ? slotLabel(sIdx)
                              : `${slotLabel(sIdx)} – ${slotLabel(eIdx)}`;
                          })();
                      return (
                        <div
                          key={`block-${v.id}`}
                          className="absolute pointer-events-auto"
                          style={{
                            top: `${topPx}px`,
                            height: `${heightPx}px`,
                            left: `calc(${TIME_COL_PX}px + (100% - ${TIME_COL_PX}px) / ${viewDays} * ${dayIdx} + (100% - ${TIME_COL_PX}px) / ${viewDays} * ${leftPct} / 100 + 2px)`,
                            width: `calc((100% - ${TIME_COL_PX}px) / ${viewDays} * ${colWidth} / 100 - 4px)`,
                            zIndex: 20,
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelTap();
                              navigate(`/site-visits/id/${v.id}`, { state: { from: pathname } });
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            className={cn(
                              "group flex h-full w-full gap-0 overflow-hidden rounded-xl border text-left",
                              "border-slate-200/90 bg-slate-50/60",
                              "shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-[box-shadow,border-color] duration-200",
                              "hover:cursor-pointer hover:border-slate-300 hover:shadow-[0_3px_10px_rgba(15,23,42,0.08)]"
                            )}
                          >
                            <div
                              className={cn("w-1 shrink-0 rounded-l-xl", accent)}
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1 px-3 py-2.5">
                              <p className="line-clamp-1 text-sm font-semibold leading-snug text-slate-900">
                                {customerName?.trim() || "—"}
                              </p>
                              <p className="mt-1.5 truncate font-mono text-[10px] font-medium tracking-tight text-slate-500">
                                {v.visitNumber}
                              </p>
                              {(addrLine || timeLine) && (
                                <p className="mt-1.5 line-clamp-2 text-[10px] leading-relaxed text-slate-400">
                                  {addrLine ? (
                                    <>
                                      <MapPin className="mr-0.5 inline h-2.5 w-2.5 -translate-y-px text-slate-400" />
                                      {addrLine}
                                    </>
                                  ) : (
                                    <span className="tabular-nums text-slate-500">{timeLine}</span>
                                  )}
                                </p>
                              )}
                              {assignees.length > 0 ? (
                                <div className="mt-2 flex flex-wrap items-center gap-0.5">
                                  {assignees.map((a: any) => (
                                    <span
                                      key={a.id ?? a.userId}
                                      title={`${a.userName} (${a.role})`}
                                      className={cn("h-1.5 w-1.5 rounded-full", ROLE_COLORS[a.role] ?? "bg-slate-400")}
                                    />
                                  ))}
                                  <span className="ml-0.5 text-[9px] font-medium text-slate-400">
                                    {STATUS_LABELS[v.status] ?? v.status}
                                  </span>
                                </div>
                              ) : (
                                <p className="mt-2 text-[9px] font-medium text-slate-400">
                                  {staffNames ? (
                                    <>{staffNames} · {STATUS_LABELS[v.status] ?? v.status}</>
                                  ) : (
                                    <>{STATUS_LABELS[v.status] ?? v.status}</>
                                  )}
                                </p>
                              )}
                            </div>
                          </button>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            </div>
          </div>
        )
        }
        {/* ── Summary strip (full calendar page only) ── */}
        {!embedded ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            {
              title: "Visits scheduled",
              value: summaryStats.scheduled,
              sub: viewDays === 7 ? "This week" : "In view range",
              icon: <CalendarDays className="h-5 w-5 text-blue-600" />,
              tint: "bg-blue-50/80 border-blue-100",
            },
            {
              title: "Team members",
              value: summaryStats.teamMembers,
              sub: "Active this week",
              icon: <Users className="h-5 w-5 text-violet-600" />,
              tint: "bg-violet-50/80 border-violet-100",
            },
            {
              title: "Completed visits",
              value: summaryStats.completed,
              sub: viewDays === 7 ? "This week" : "In view range",
              icon: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
              tint: "bg-emerald-50/80 border-emerald-100",
            },
            {
              title: "Pending visits",
              value: summaryStats.pending,
              sub: viewDays === 7 ? "This week" : "In view range",
              icon: <Clock className="h-5 w-5 text-amber-600" />,
              tint: "bg-amber-50/80 border-amber-100",
            },
          ].map((row) => (
            <Card key={row.title} className={`border shadow-none ${row.tint}`}>
              <CardContent className="flex items-start gap-3 px-4 py-4">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/90 shadow-sm ring-1 ring-slate-100">
                  {row.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-600">{row.title}</p>
                  <p className="text-2xl font-semibold tracking-tight text-slate-900">{row.value}</p>
                  <p className="text-[11px] text-slate-500">{row.sub}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        ) : null}

      </div>

      {/* ── Schedule Visit Modal ── */}
      <Dialog open={panelOpen} onOpenChange={(open) => { if (!open) { setPanelOpen(false); resetPanel(); } }}>
        <DialogContent className="max-w-md w-full p-0 overflow-hidden">
          <div className="px-6 pt-5 pb-4 border-b">
            <DialogTitle className="text-base font-semibold">Schedule Visit</DialogTitle>
            {selectedDate && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatSingaporeDate(selectedDate, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                {timeLabel ? ` · ${timeLabel}` : ""}
              </p>
            )}
          </div>

          <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Visit Type */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Visit Type</p>
              <div className="flex gap-2">
                {(["new_install", "service_visit"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setVisitType(type)}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors font-medium ${
                      visitType === type
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {type === "new_install" ? "New Install" : "Service Visit"}
                  </button>
                ))}
              </div>
            </div>

            {/* Customer */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Customer *</p>
              {selectedCustomer ? (
                <div className="flex items-center justify-between gap-2 p-2.5 border rounded-md bg-gray-50">
                  <div>
                    <p className="text-sm font-medium">{selectedCustomer.name}</p>
                    {fullAddress && <p className="text-xs text-muted-foreground mt-0.5">{fullAddress}</p>}
                  </div>
                  <button onClick={() => { setSelectedCustomer(null); setCustomerQuery(""); }} className="p-1 rounded hover:bg-gray-200">
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={customerQuery} onChange={(e) => { setCustomerQuery(e.target.value); setShowDropdown(true); }} onFocus={() => setShowDropdown(true)} placeholder="Search by name or contact..." className="pl-9" />
                  </div>
                  {showDropdown && customerQuery.length >= 2 && !selectedCustomer && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-white border rounded-md shadow-lg max-h-52 overflow-y-auto">
                      {searchResults && searchResults.length > 0 ? (
                        searchResults.map((c) => (
                          <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm" onClick={() => { setSelectedCustomer(c); setCustomerQuery(c.name); setShowDropdown(false); }}>
                            <p className="font-medium">{c.name}</p>
                            {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No customers found for &ldquo;{customerQuery}&rdquo;</div>
                      )}
                      <button className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-t flex items-center gap-2 text-blue-600 font-medium" onMouseDown={(e) => { e.preventDefault(); setShowDropdown(false); setNewCustForm(f => ({ ...f, name: customerQuery })); setShowNewCustomerModal(true); }}>
                        <UserPlus className="h-4 w-4" />Create new customer &ldquo;{customerQuery}&rdquo;
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Assign Team */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">Assign Team</p>

              {technicians.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">TECHNICIANS</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {technicians.map((u) => {
                      const checked = selectedAssignees.some(a => a.userId === u.id);
                      const leaveInfo = getLeaveForUser(u.id, selectedDate, leavePeriodForWindow(arrivalWindow));
                      const isOnLeave = Boolean(leaveInfo);
                      return (
                        <label key={u.id} className={cn("flex items-center gap-2", isOnLeave ? "cursor-not-allowed opacity-60" : "cursor-pointer")}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isOnLeave}
                            onChange={() => {
                              if (isOnLeave) return;
                              toggleAssignee(u.id, u.name ?? "", "technician");
                            }}
                            className="w-4 h-4 rounded accent-amber-500"
                          />
                          <span className="text-sm">{u.name}{isOnLeave ? " (on leave)" : ""}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {programmers.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">PROGRAMMERS</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {programmers.map((u) => {
                      const checked = selectedAssignees.some(a => a.userId === u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={() => toggleAssignee(u.id, u.name ?? "", "programmer")} className="w-4 h-4 rounded accent-amber-500" />
                          <span className="text-sm">{u.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {installers.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">INSTALLERS</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {installers.map((u) => {
                      const checked = selectedAssignees.some(a => a.userId === u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={() => toggleAssignee(u.id, u.name ?? "", "installer")} className="w-4 h-4 rounded accent-amber-500" />
                          <span className="text-sm">{u.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Conflict warnings */}
              {selectedAssignees.map(a => {
                const conflict = hasConflict(a.userId);
                if (!conflict) return null;
                return (
                  <div key={a.userId} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800 mt-2">
                    <span>⚠️</span>
                    <span><strong>{a.userName}</strong> has a conflicting schedule at this time ({conflict.visitNumber}).</span>
                  </div>
                );
              })}

            </div>

            {/* Scope of Work */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Scope of Work <span className="text-red-500">*</span></p>
              <Textarea
                value={complaintDescription}
                onChange={(e) => setComplaintDescription(e.target.value)}
                placeholder="Describe the scope of work for this visit..."
                rows={3}
              />
              {!complaintDescription.trim() && (
                <p className="text-xs text-amber-600 mt-1">Required — technicians need to know what to do before arriving.</p>
              )}
            </div>
            <div className="space-y-2 rounded-md border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={preAgreedChargeEnabled}
                  onChange={(e) => {
                    setPreAgreedChargeEnabled(e.target.checked);
                    if (!e.target.checked) setPreAgreedChargeAmount("");
                  }}
                  className="h-4 w-4 rounded accent-amber-500"
                />
                Pre-agreed Visit Charge
              </label>
              {preAgreedChargeEnabled && (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={preAgreedChargeAmount}
                  onChange={(e) => setPreAgreedChargeAmount(e.target.value)}
                  placeholder="Amount (required)"
                  className="w-full border border-gray-200 px-3 py-2 text-sm rounded-md focus:outline-none focus:border-black"
                />
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => { setPanelOpen(false); resetPanel(); }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={createVisit.isPending || !selectedCustomer || !selectedDate || !complaintDescription.trim()}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white border-0"
            >
              {createVisit.isPending ? "Scheduling…" : "Confirm Slot"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={handleLeaveDialogChange}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Block Technician — ON LEAVE</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This does not automatically link to the leave system. Please apply for your leave separately.
            </p>
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Technician</p>
              {isLeaveAdmin ? (
                <select
                  value={leaveUserId ?? ""}
                  onChange={(e) => setLeaveUserId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="">Select technician</option>
                  {leaveScheduleUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <Input
                    readOnly
                    disabled
                    value={currentUser?.name ?? currentUser?.email ?? (currentUser?.id ? `User #${currentUser.id}` : "")}
                    className="bg-muted/50 text-muted-foreground"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Your leave only — this cannot be changed. WhatsApp alerts still go to PICs if you have conflicting visits.
                  </p>
                </>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</p>
              <Input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)} />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Period</p>
              <div className="flex gap-2">
                {([
                  { id: "full_day", label: "Full Day" },
                  { id: "am", label: "AM" },
                  { id: "pm", label: "PM" },
                ] as const).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setLeavePeriod(p.id)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-sm",
                      leavePeriod === p.id ? "border-black bg-black text-white" : "border-gray-200 text-gray-700"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</p>
              <Textarea value={leaveNotes} onChange={(e) => setLeaveNotes(e.target.value)} rows={3} placeholder="Optional notes" />
            </div>

            <div className="border-t border-gray-100 pt-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Existing leave</p>
              {!leaveUserId ? (
                <p className="text-xs text-muted-foreground">Select a technician to view scheduled leave.</p>
              ) : managedLeaveRows.length === 0 ? (
                <p className="text-xs text-muted-foreground">No leave applied yet</p>
              ) : (
                <>
                  <div className="max-h-48 overflow-y-auto space-y-2 rounded-md border border-gray-100 p-2">
                    {managedLeaveRows.map((row) => (
                      <div key={row.id} className="flex gap-2 items-start text-xs border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                        <Checkbox
                          checked={selectedLeaveIds.includes(row.id)}
                          onCheckedChange={(v) => {
                            const on = v === true;
                            setSelectedLeaveIds((prev) =>
                              on ? [...prev.filter((x) => x !== row.id), row.id] : prev.filter((x) => x !== row.id)
                            );
                          }}
                          className="mt-0.5"
                          aria-label={`Select leave ${row.id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900">
                            {formatSingaporeDate(row.leaveDate, {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}{" "}
                            · {row.period === "full_day" ? "Full day" : row.period === "am" ? "AM" : "PM"}
                          </p>
                          {row.notes ? <p className="text-gray-600 mt-0.5 line-clamp-2">{row.notes}</p> : null}
                          <p className="text-gray-400 mt-0.5 text-[10px]">
                            Created{" "}
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" })
                              : "—"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="w-full"
                    disabled={deleteLeaveBlocks.isPending || selectedLeaveIds.length === 0}
                    onClick={() => {
                      if (selectedLeaveIds.length === 0) return;
                      if (!confirm(`Cancel ${selectedLeaveIds.length} selected leave block(s)?`)) return;
                      deleteLeaveBlocks.mutate({ ids: selectedLeaveIds });
                    }}
                  >
                    {deleteLeaveBlocks.isPending ? "Cancelling…" : "Cancel selected leave"}
                  </Button>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>Close</Button>
            <Button
              onClick={() => {
                if (!leaveUserId || !leaveDate) {
                  toast.error("Please select technician and date");
                  return;
                }
                createLeave.mutate({
                  userId: leaveUserId,
                  leaveDate: parseSingaporeDateInput(leaveDate),
                  period: leavePeriod,
                  notes: leaveNotes || undefined,
                });
              }}
              disabled={createLeave.isPending}
            >
              {createLeave.isPending ? "Saving..." : "Save leave block"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Inline Create New Customer Modal ── */}
      <Dialog open={showNewCustomerModal} onOpenChange={(open) => { if (!open) { setShowNewCustomerModal(false); setNewCustForm({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false }); setNewCustAddressQuery(""); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" />Create New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (!newCustForm.name.trim()) return; createCustomerInline.mutate({ name: newCustForm.name.trim(), contact: newCustForm.contact.trim() || undefined, email: newCustForm.email.trim() || undefined, address: newCustForm.address.trim() || undefined, unitNumber: newCustForm.unitNumber.trim() || undefined, postalCode: newCustForm.postalCode.trim() || undefined, notes: newCustForm.notes.trim() || undefined, creationSource: "CALENDAR" }); }} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Full Name / Company Name <span className="text-red-500">*</span></label>
              <input type="text" value={newCustForm.name} onChange={(e) => setNewCustForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Chewbacca Chan" required className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Contact Number</label>
                <input type="text" value={newCustForm.contact} onChange={(e) => setNewCustForm(f => ({ ...f, contact: e.target.value }))} placeholder="e.g. 91234567" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Email Address</label>
                <input type="email" value={newCustForm.email} onChange={(e) => setNewCustForm(f => ({ ...f, email: e.target.value }))} placeholder="e.g. chan@email.com" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none" />
              </div>
            </div>
            <AddressSearchInput value={newCustAddressQuery} onAddressSelect={(address, postalCode) => { setNewCustForm(f => ({ ...f, address, postalCode })); setNewCustAddressQuery(address); }} />
            {newCustForm.address && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Is this a condo or apartment building?</label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setNewCustForm(f => ({ ...f, isCondo: true }))} className={`flex-1 px-3 py-2 text-sm border transition-colors ${newCustForm.isCondo ? "border-black bg-black text-white" : "border-gray-200 text-gray-700 hover:border-black"}`}>Yes</button>
                  <button type="button" onClick={() => setNewCustForm(f => ({ ...f, isCondo: false }))} className={`flex-1 px-3 py-2 text-sm border transition-colors ${!newCustForm.isCondo && newCustForm.address ? "border-black bg-black text-white" : "border-gray-200 text-gray-700 hover:border-black"}`}>No</button>
                </div>
              </div>
            )}
            {newCustForm.isCondo && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Unit / Floor</label>
                <input type="text" value={newCustForm.unitNumber} onChange={(e) => setNewCustForm(f => ({ ...f, unitNumber: e.target.value }))} placeholder="e.g. #03-12" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Notes</label>
              <textarea value={newCustForm.notes} onChange={(e) => setNewCustForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes about this customer..." rows={2} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-none resize-none" />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <button type="button" onClick={() => { setShowNewCustomerModal(false); }} className="px-4 h-9 text-xs border border-gray-200 text-gray-600 hover:border-black hover:text-black transition-colors">Cancel</button>
              <button type="submit" disabled={createCustomerInline.isPending || !newCustForm.name.trim()} className="px-6 h-9 bg-black text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {createCustomerInline.isPending ? "Creating…" : "Create & Select Customer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </>
  );

  if (embedded) return calendarInner;
  return <DashboardLayout>{calendarInner}</DashboardLayout>;
}

const FIELD_CALENDAR_ROLES = new Set(["service", "programming", "installer"]);

function CalendarPage() {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const [checkedRedirect, setCheckedRedirect] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (checkedRedirect) return;
    if (!user || !FIELD_CALENDAR_ROLES.has(user.role ?? "")) {
      setCheckedRedirect(true);
      return;
    }
    if (pathname === "/calendar") {
      navigate("/calendar/field");
      return;
    }
    setCheckedRedirect(true);
  }, [user, loading, pathname, checkedRedirect]);

  if (
    loading ||
    (!checkedRedirect && user && FIELD_CALENDAR_ROLES.has(user.role ?? ""))
  ) {
    return null;
  }

  return <TeamCalendar />;
}

export default CalendarPage;
